import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";
import { normalizeBlobError, parseContentDispositionFilename } from "@/shared/utils/downloadBlob";

import { AUDIT_API_ENDPOINTS } from "../constants/auditoria.constants";
import type {
  GetAuditResponse,
  AuditEntityHistoryResponse,
  PaymentAuditResponse,
  ClientOrganizationSearchResult,
  OrganizationByEmailResponse,
  ClientByEmailResponse,
} from "../types/auditoria-api.types";
import type { AuditoriaFiltersState, PaymentAuditFiltersState } from "../types/auditoria.types";

function parseFilterAmount(raw: string | undefined): string | undefined {
  const t = raw?.trim().replace(",", ".") ?? "";
  if (!t) return undefined;
  const n = Number(t);
  if (!Number.isFinite(n)) return undefined;
  return String(n);
}

export interface AuditApiParams {
  skip: number;
  limit: number;
  entity_type?: string;
  action?: string;
  start_date?: string;
  end_date?: string;
  description?: string;
  payment_client_id?: string;
  payment_organization_id?: string;
  payment_currency?: string;
  payment_amount_min?: string;
  payment_amount_max?: string;
  confirmation_source?: string;
}

/** Builds query params for the API from filter state and pagination (page, pageSize). */
export function buildAuditQueryParams(
  filters: AuditoriaFiltersState,
  page: number,
  pageSize: number,
): AuditApiParams {
  const skip = (page - 1) * pageSize;
  const params: AuditApiParams = { skip, limit: pageSize };

  if (filters.flujo) {
    params.entity_type = filters.flujo;
  }
  if (filters.accion) {
    params.action = filters.accion;
  }
  if (filters.fechaInicio) {
    params.start_date = filters.fechaInicio.includes("T")
      ? filters.fechaInicio
      : `${filters.fechaInicio}T00:00:00.000Z`;
  }
  if (filters.fechaFinal) {
    params.end_date = filters.fechaFinal.includes("T")
      ? filters.fechaFinal
      : `${filters.fechaFinal}T23:59:59.999Z`;
  }
  const searchTrimmed = filters.search?.trim();
  if (searchTrimmed) {
    params.description = searchTrimmed;
  }
  const clientIdTrimmed = filters.clientId?.trim();
  const organizationIdTrimmed = filters.organizationId?.trim();
  if (clientIdTrimmed) {
    params.payment_client_id = clientIdTrimmed;
  } else if (organizationIdTrimmed) {
    params.payment_organization_id = organizationIdTrimmed;
  }
  const paymentCurrencyTrimmed = filters.paymentCurrency?.trim();
  if (paymentCurrencyTrimmed) {
    params.payment_currency = paymentCurrencyTrimmed;
  }
  const amountMin = parseFilterAmount(filters.paymentAmountMin);
  if (amountMin !== undefined) {
    params.payment_amount_min = amountMin;
  }
  const amountMax = parseFilterAmount(filters.paymentAmountMax);
  if (amountMax !== undefined) {
    params.payment_amount_max = amountMax;
  }
  const confirmationSourceTrimmed = filters.confirmationSource?.trim();
  if (confirmationSourceTrimmed) {
    params.confirmation_source = confirmationSourceTrimmed;
  }

  return params;
}

export interface PaymentAuditApiParams {
  skip: number;
  limit: number;
  organization_name?: string;
  client_name?: string;
  currency?: string;
  amount_min?: string;
  amount_max?: string;
  start_date?: string;
  end_date?: string;
  payment_source?: string;
}

export type PaymentAuditFilterParams = Omit<PaymentAuditApiParams, "skip" | "limit">;

/**
 * The applied filters as API params: the one place the payment-audit slice is defined, shared by the list and
 * the Excel report so the two can never describe different rows.
 */
export function buildPaymentAuditFilterParams(filters: PaymentAuditFiltersState): PaymentAuditFilterParams {
  const value = (raw: string) => raw.trim() || undefined;
  const startDate = value(filters.start_date);
  const endDate = value(filters.end_date);
  return {
    organization_name: value(filters.organization_name),
    client_name: value(filters.client_name),
    currency: value(filters.currency),
    amount_min: value(filters.amount_min),
    amount_max: value(filters.amount_max),
    start_date: startDate && `${startDate}T00:00:00.000Z`,
    end_date: endDate && `${endDate}T23:59:59.999Z`,
    payment_source: value(filters.payment_source),
  };
}

export interface PaymentAuditReport {
  blob: Blob;
  filename: string;
}

/**
 * The filters match more records than one report may carry, so the backend produced no file at all rather
 * than a partial one. Recognised by `code`, because the count has to be worded in the reader's language and
 * the dashboard already knows it.
 */
export const PAYMENT_AUDIT_EXPORT_TOO_LARGE_CODE = "payment_audit_export_too_large";

/** The filter half of the payment-audit query string. Pagination is added by the caller that needs it. */
function buildPaymentAuditFilterQuery(params: PaymentAuditFilterParams): URLSearchParams {
  const search = new URLSearchParams();
  if (params.organization_name) search.set("organization_name", params.organization_name);
  if (params.client_name) search.set("client_name", params.client_name);
  if (params.currency) search.set("currency", params.currency);
  if (params.amount_min) search.set("amount_min", params.amount_min);
  if (params.amount_max) search.set("amount_max", params.amount_max);
  if (params.start_date) search.set("start_date", params.start_date);
  if (params.end_date) search.set("end_date", params.end_date);
  if (params.payment_source) search.set("payment_source", params.payment_source);
  return search;
}

function defaultPaymentAuditReportName(): string {
  const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  return `solicitudes-de-pago-${datePart}.xlsx`;
}

export class AuditoriaService {
  /**
   * Fetches audit records with filters and pagination. Endpoint: GET /internal/operations/audit (base URL
   * already includes /api/v1).
   */
  static async fetchAudit(params: AuditApiParams): Promise<AxiosResponse<GetAuditResponse>> {
    const search = new URLSearchParams();

    search.set("skip", String(params.skip));
    search.set("limit", String(params.limit));
    if (params.entity_type) search.set("entity_type", params.entity_type);
    if (params.action) search.set("action", params.action);
    if (params.start_date) search.set("start_date", params.start_date);
    if (params.end_date) search.set("end_date", params.end_date);
    if (params.description) search.set("description", params.description);
    if (params.payment_client_id) search.set("payment_client_id", params.payment_client_id);
    if (params.payment_organization_id) search.set("payment_organization_id", params.payment_organization_id);
    if (params.payment_currency) search.set("payment_currency", params.payment_currency);
    if (params.payment_amount_min) search.set("payment_amount_min", params.payment_amount_min);
    if (params.payment_amount_max) search.set("payment_amount_max", params.payment_amount_max);
    if (params.confirmation_source) search.set("confirmation_source", params.confirmation_source);

    const url = `${AUDIT_API_ENDPOINTS.AUDIT}?${search.toString()}`;
    return HttpApiService.get<GetAuditResponse>(url);
  }

  /**
   * Fetches entity history (for the "View history" modal). Endpoint: GET
   * /internal/operations/audit/entity/{type}/{id} (base URL already includes /api/v1).
   */
  static async fetchPaymentAudit(
    params: PaymentAuditApiParams,
  ): Promise<AxiosResponse<PaymentAuditResponse>> {
    const search = buildPaymentAuditFilterQuery(params);
    search.set("skip", String(params.skip));
    search.set("limit", String(params.limit));
    const url = `${AUDIT_API_ENDPOINTS.PAYMENT_AUDIT}?${search.toString()}`;
    return HttpApiService.get<PaymentAuditResponse>(url);
  }

  /**
   * The same filters as `fetchPaymentAudit`, answered as an .xlsx report. `skip` / `limit` are deliberately
   * NOT sent: the report carries the whole filtered set, not the page on screen. A file that arrives is
   * therefore always complete — past the backend's cap the request fails with
   * `PAYMENT_AUDIT_EXPORT_TOO_LARGE_CODE` and nothing is downloaded.
   */
  static async downloadPaymentAuditReport(params: PaymentAuditFilterParams): Promise<PaymentAuditReport> {
    const search = buildPaymentAuditFilterQuery(params);
    const query = search.toString();
    const url = query
      ? `${AUDIT_API_ENDPOINTS.PAYMENT_AUDIT_EXPORT}?${query}`
      : AUDIT_API_ENDPOINTS.PAYMENT_AUDIT_EXPORT;

    try {
      const response = await HttpApiService.get<Blob>(url, { responseType: "blob" });
      const headers = response.headers as Record<string, string | undefined>;
      return {
        blob: response.data,
        filename:
          parseContentDispositionFilename(headers["content-disposition"]) ?? defaultPaymentAuditReportName(),
      };
    } catch (error: unknown) {
      // With `responseType: "blob"` the ERROR body is a blob too, so the usual
      // `{ message, code }` is unreadable until it is parsed back.
      throw await normalizeBlobError(error);
    }
  }

  static async fetchClientsSearch(params: {
    organization_name?: string;
    client_name?: string;
  }): Promise<AxiosResponse<ClientOrganizationSearchResult>> {
    const search = new URLSearchParams();
    if (params.organization_name) search.set("organization_name", params.organization_name);
    if (params.client_name) search.set("client_name", params.client_name);
    const url = `${AUDIT_API_ENDPOINTS.CLIENTS_SEARCH}?${search.toString()}`;
    return HttpApiService.get<ClientOrganizationSearchResult>(url);
  }

  static async fetchEntityHistory(
    entityType: string,
    entityId: string,
  ): Promise<AxiosResponse<AuditEntityHistoryResponse>> {
    const path = AUDIT_API_ENDPOINTS.entityHistory(entityType, entityId);
    return HttpApiService.get<AuditEntityHistoryResponse>(path);
  }

  static async fetchOrganizationByEmail(email: string): Promise<AxiosResponse<OrganizationByEmailResponse>> {
    const search = new URLSearchParams();
    search.set("email", email);
    const url = `${AUDIT_API_ENDPOINTS.ORGANIZATION_BY_EMAIL}?${search.toString()}`;
    return HttpApiService.get<OrganizationByEmailResponse>(url);
  }

  static async fetchClientByEmail(email: string): Promise<AxiosResponse<ClientByEmailResponse>> {
    const search = new URLSearchParams();
    search.set("email", email);
    const url = `${AUDIT_API_ENDPOINTS.CLIENT_BY_EMAIL}?${search.toString()}`;
    return HttpApiService.get<ClientByEmailResponse>(url);
  }
}
